import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import Api, { paths } from 'shared/services/Api'
import {
  AuthData,
  SignInCognitoResponse,
  NewPasswordChallenge,
  SignInUserSession,
  CurrentUserInfo,
  CognitoError,
  UserType,
} from 'shared/types'
import { WrongUserTypeError } from 'shared/utils'
import { EditUserInfoPayload } from './action.types'

const NAMESPACE = 'auth'

export const clearErrors = createAction(`${NAMESPACE}/clearErrors`)

export const login = createAsyncThunk<
  SignInUserSession | NewPasswordChallenge,
  AuthData,
  { rejectValue: string }
>(`${NAMESPACE}/login`, async (data, thunkAPI) => {
  try {
    const response: SignInCognitoResponse | NewPasswordChallenge =
      await Auth.signIn(data)
    if (
      (response as NewPasswordChallenge).challengeName ===
      'NEW_PASSWORD_REQUIRED'
    )
      return response as NewPasswordChallenge

    const { signInUserSession, attributes } = response as SignInCognitoResponse
    if (
      [UserType.Admin, UserType.SuperAdmin].includes(
        attributes['custom:role'] as UserType
      )
    ) {
      await Auth.signOut()
      throw new WrongUserTypeError()
    }

    return signInUserSession
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message)
  }
})

export const setNewPassword = createAsyncThunk<
  SignInUserSession,
  { user: any; password: string },
  { rejectValue: string }
>(
  `${NAMESPACE}/setNewPassword`,
  async ({ user, password }, { rejectWithValue }) => {
    try {
      const { signInUserSession }: SignInCognitoResponse =
        await Auth.completeNewPassword(user, password)
      return signInUserSession
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const register = createAsyncThunk<
  any,
  { emailAddress: string; password: string; language: string },
  { rejectValue: string }
>(`${NAMESPACE}/registerUser`, async (data, thunkAPI) => {
  try {
    const response = await Api.post(paths.Users.register, data, {
      withCredentials: false,
    })
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const logout = createAsyncThunk(
  `${NAMESPACE}/logout`,
  async (_, thunkAPI) => {
    try {
      await Auth.signOut()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const getCurrentAuthenticatedUser = createAsyncThunk(
  `${NAMESPACE}/getCurrentAuthenticatedUser`,
  async (_, thunkAPI) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      return user
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const sendForgotPasswordEmail = createAsyncThunk<
  undefined,
  { email: string; language: string }
>(
  `${NAMESPACE}/sendForgotPasswordEmail`,
  async ({ email, language }, thunkAPI) => {
    try {
      await Auth.forgotPassword(email, { language })
      return
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const resendActivationEmail = createAsyncThunk<
  undefined,
  { email: string; language: string }
>(
  `${NAMESPACE}/resendActivationEmail`,
  async ({ email, language }, thunkAPI) => {
    try {
      await Auth.resendSignUp(email, { language })
      return
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const resetPassword = createAsyncThunk<
  undefined,
  { username: string; confirmationCode: string; password: string },
  { rejectValue: string }
>(
  `${NAMESPACE}/forgotPasswordSubmit`,
  async ({ username, confirmationCode, password }, thunkAPI) => {
    try {
      await Auth.forgotPasswordSubmit(username, confirmationCode, password)
      return
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const confirm = createAsyncThunk<
  undefined,
  { username: string; confirmationCode: string },
  { rejectValue: string }
>(`${NAMESPACE}/confirm`, async ({ username, confirmationCode }, thunkAPI) => {
  try {
    await Auth.confirmSignUp(username, confirmationCode)
    return
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getCurrentUserInfo = createAsyncThunk<
  CurrentUserInfo,
  undefined,
  { rejectValue: string }
>(`${NAMESPACE}/getCurrentUserInfo`, async (_, thunkAPI) => {
  try {
    const info: CurrentUserInfo = await Auth.currentUserInfo()
    if (!Object.keys(info || {}).length)
      throw new Error(CognitoError.UserNotFound)
    return info
  } catch (error) {
    if (error?.message === CognitoError.UserNotFound) {
      await Auth.signOut()
      window.location.reload()
    }
    return thunkAPI.rejectWithValue(error)
  }
})

export const editUserInfo = createAsyncThunk<
  void,
  EditUserInfoPayload,
  { rejectValue: string }
>(`${NAMESPACE}/editUserInfo`, async (request, thunkAPI) => {
  try {
    const response = await Api.put(paths.Users.root, request)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

const actions = {
  clearErrors,
  login,
  logout,
  register,
  getCurrentAuthenticatedUser,
  sendForgotPasswordEmail,
  getCurrentUserInfo,
  resetPassword,
}

export default actions
